import {
  Alert,
  Box,
  Card,
  CardContent,
  SelectChangeEvent,
  Stack,
  Typography
} from "@mui/material";
import React, { useMemo, useState } from "react";
import {
  useGetMyOpportunitiesQuery
} from "../../../../store/services/opportunityService";
import OpportunityCardSkeleton from "../../card/OpportunityCardSkeleton";
import OpportunityItem from "../../card/OpportunityItem";
import OpportunityMenu from "../../card/OpportunityMenu";
import FilterSection from "./FilterSection";
import { calculateStatistics } from "./helpers";
import StatisticsCards from "./StatisticsCards";
import { FilterOptions } from "./types";

const UserOpportunitiesView: React.FC = () => {
  console.log("asdsd")

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  // Filter state
  const [filters, setFilters] = useState<FilterOptions>({
    startDate: null,
    endDate: null,
    sortBy: "newest",
    searchTerm: "",
    assignedTo: "",
  });


  const {
    data: opportunities = [],
    isLoading,
    error,
  } = useGetMyOpportunitiesQuery();

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleDateChange =
    (field: keyof FilterOptions) => (newValue: Date | null) => {
      setFilters({
        ...filters,
        [field]: newValue,
      });
    };

  const handleSortChange = (event: SelectChangeEvent) => {
    setFilters({
      ...filters,
      sortBy: event.target.value,
    });
  };

  // Sort and filter opportunities
  const filteredAndSortedOpportunities = useMemo(() => {
    // First apply search filter
    let filtered = opportunities;

    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (opp) =>
          opp.OpportunityName.toLowerCase().includes(searchLower) ||
          (opp.OpportunityDescription &&
            opp.OpportunityDescription.toLowerCase().includes(searchLower)) ||
          (opp.CustomerName &&
            opp.CustomerName.toLowerCase().includes(searchLower))
      );
    }

    // Filter by assigned user
    if (filters.assignedTo) {
      filtered = filtered.filter(
        (opp) => opp.AssignedToName === filters.assignedTo
      );
    }

    // Then sort
    return [...filtered].sort((a, b) => {
      switch (filters.sortBy) {
        case "newest":
          return new Date(b.Created).getTime() - new Date(a.Created).getTime();
        case "oldest":
          return new Date(a.Created).getTime() - new Date(b.Created).getTime();
        case "value-high":
          return (
            (b.OpportunityTotalAmount || 0) - (a.OpportunityTotalAmount || 0)
          );
        case "value-low":
          return (
            (a.OpportunityTotalAmount || 0) - (b.OpportunityTotalAmount || 0)
          );
        case "probability-high":
          return (
            (b.OpportunityProbability || 0) - (a.OpportunityProbability || 0)
          );
        case "probability-low":
          return (
            (a.OpportunityProbability || 0) - (b.OpportunityProbability || 0)
          );
        case "name-asc":
          return a.OpportunityName.localeCompare(b.OpportunityName);
        case "name-desc":
          return b.OpportunityName.localeCompare(a.OpportunityName);
        default:
          return 0;
      }
    });
  }, [opportunities, filters]);

  // Beräkna statistik
  const stats = useMemo(
    () => calculateStatistics(filteredAndSortedOpportunities),
    [filteredAndSortedOpportunities]
  );

  if (isLoading) {
    return <OpportunityCardSkeleton />;
  }

  if (error) {
    return (
      <Card elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <CardContent>
          <Alert severity="error">
            Ett fel uppstod vid hämtning av Affärsmöjligheter
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <StatisticsCards
        stats={stats}
        filteredAndSortedOpportunities={filteredAndSortedOpportunities}
      />
      <Card elevation={2} sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>


        <FilterSection
          filters={filters}
          handleDateChange={handleDateChange}
          handleSortChange={handleSortChange}
        />

        <CardContent sx={{ p: 0 }}>
          {filteredAndSortedOpportunities.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                Inga Affärsmöjligheter hittades
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Justera filtren eller välj ett annat datumintervall
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0}>
              {filteredAndSortedOpportunities.map((opportunity, idx) => (
                <OpportunityItem
                  index={idx}
                  key={opportunity.OpportunityNo}
                  opportunity={opportunity}
                  onMenuOpen={(e) =>
                    handleMenuOpen(e)
                  }
                />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <OpportunityMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      />
    </>
  );
};

export default UserOpportunitiesView;
